import type { HotfixProposal, HotfixRequest } from '@/types/hotfixRequests';
import type { ReleaseTag } from '@/types';

export const HOTFIX_REQUEST_APPROVING_POLL_INTERVAL_MS = 12_000;

export const hotfixRequestPollingInterval = (status?: HotfixRequest['status']): number =>
    status === 'approving' ? HOTFIX_REQUEST_APPROVING_POLL_INTERVAL_MS : 0;

export const isActiveHotfixRequest = (request: HotfixRequest): boolean =>
    request.status === 'pending' || request.status === 'approving';

export const hotfixRequestStatusLabel = (status: HotfixRequest['status']): string => {
    switch (status) {
        case 'pending':
            return 'Pending approval';
        case 'approving':
            return 'Creating release';
        case 'approved':
            return 'Approved';
        case 'rejected':
            return 'Rejected';
        case 'cancelled':
            return 'Cancelled';
    }
};

export const canNavigateToApprovedRelease = (request: HotfixRequest): boolean =>
    request.status === 'approved' && !!request.resultingReleaseId;

export const resultingReleaseRouteParam = (appId: string, resultingReleaseId: string): string => {
    const prefix = `${appId}:`;
    return resultingReleaseId.startsWith(prefix) ? resultingReleaseId.slice(prefix.length) : resultingReleaseId;
};

export const isHotfixRequestConflict = (error: unknown): boolean => {
    if (typeof error !== 'object' || error === null || !('data' in error)) return false;
    const data = error.data;
    return (
        typeof data === 'object' &&
        data !== null &&
        'message' in data &&
        typeof data.message === 'string' &&
        data.message.toLowerCase().includes('changed')
    );
};

export interface HotfixProposalReviewValues {
    releaseVersion: string;
    releaseName?: string;
    releaseType: 'hotfix' | 'rollback';
    baseForHotfix?: string;
    shouldCreateBranch: boolean;
    shouldBumpVersionOnBranch: boolean;
    commitHashes: string[];
    sourceVersion?: string;
    resigningStrategy?: HotfixProposal['rollbackResigningStrategy'];
    shouldMatchPreviousPhasedRelease: boolean;
    allDestinations: boolean;
    destinationAction: 'include' | 'exclude';
    destinationIds: string[];
    formFields?: Record<string, string>;
}

export const proposalToReviewValues = (proposal: HotfixProposal): HotfixProposalReviewValues => ({
    releaseVersion: proposal.proposedVersion,
    releaseName: proposal.releaseName,
    releaseType: proposal.releaseType,
    baseForHotfix: proposal.base?.name,
    shouldCreateBranch: proposal.shouldCreateBranch,
    shouldBumpVersionOnBranch: proposal.shouldBumpVersionOnBranch,
    commitHashes: proposal.commitsToCherryPick ?? [],
    sourceVersion: proposal.rollbackSourceVersion,
    resigningStrategy: proposal.rollbackResigningStrategy,
    shouldMatchPreviousPhasedRelease: proposal.shouldMatchPreviousPhasedRelease,
    allDestinations: !proposal.destinationOptions,
    destinationAction: proposal.destinationOptions?.action ?? 'include',
    destinationIds: proposal.destinationOptions?.destinationIds ?? [],
});

export const reviewValuesToProposal = (
    values: HotfixProposalReviewValues,
    source: HotfixProposal,
    bases: ReleaseTag[] = [],
): HotfixProposal => {
    const hotfix = values.releaseType === 'hotfix';
    const selectedBase =
        bases.find((base) => base.name === values.baseForHotfix) ??
        (source.base?.name === values.baseForHotfix ? source.base : undefined);
    return {
        releaseType: values.releaseType,
        proposedVersion: values.releaseVersion.trim(),
        ...(values.releaseName?.trim() ? { releaseName: values.releaseName.trim() } : {}),
        ...(hotfix && selectedBase ? { base: selectedBase } : {}),
        shouldCreateBranch: hotfix && values.shouldCreateBranch,
        shouldBumpVersionOnBranch: hotfix && values.shouldBumpVersionOnBranch,
        commitsToCherryPick: hotfix ? (values.commitHashes ?? []) : [],
        ...(!hotfix ?
            {
                rollbackSourceVersion: values.sourceVersion?.trim(),
                rollbackResigningStrategy: values.resigningStrategy,
            }
        :   {}),
        shouldMatchPreviousPhasedRelease: hotfix && values.shouldMatchPreviousPhasedRelease,
        ...(!values.allDestinations && values.destinationIds?.length ?
            { destinationOptions: { action: values.destinationAction, destinationIds: values.destinationIds } }
        :   {}),
    };
};

export const buildHotfixApprovalPayload = (
    request: HotfixRequest,
    values: HotfixProposalReviewValues,
    source: HotfixProposal,
    bases: ReleaseTag[] = [],
) => ({
    expectedRevision: request.revision,
    finalProposal: reviewValuesToProposal(values, source, bases),
});
